import { Button, InputAdornment, TableCell, TableRow } from '@mui/material'
import { useCallback, useContext, useEffect, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useSelectPricingMatrix } from '../../../hooks/useSelectPricingMatrix/useSelectPricingMatrix'
import { CounterSalesContext } from '../../../providers/CounterSalesProvider/CounterSalesProvider'
import {
  useCreateCounterSaleNoTagsMutation,
  useGetVendorEntriesByIdQuery,
  useUpdateInventoryInstanceMutation
} from '../../../redux/api'
import {
  CounterSaleInfoStatusEnum,
  InventoryPartInstanceEntry
} from '../../../sdk'
import { filterMarginsWithEntries } from '../../../utils/handleMargins/handleMargins'
import { roundToTwoDecimal } from '../../../utils/roundToTwoDecimal/roundToTwoDecimal'
import TextFieldNoArrowNoScroll from '../../FormField/TextFieldNoArrowNoScroll'

interface Props {
  instance: InventoryPartInstanceEntry
  row: number
  isFluids: boolean
  closeDialog: () => void
}

const CounterSaleInstanceItem = ({
  instance,
  row,
  isFluids,
  closeDialog
}: Props) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { activeCounterSale } = useContext(CounterSalesContext)
  const [updateInventoryInstance, { isLoading: isLoadingUpdateInventory }] =
    useUpdateInventoryInstanceMutation()
  const [createCounterSale, { isLoading: isLoadingCreateCounterSale }] =
    useCreateCounterSaleNoTagsMutation()

  const { selectedPartsMatrix } = useSelectPricingMatrix('', '', '')

  const [price, setPrice] = useState<number | ''>('')

  useEffect(() => {
    if (!selectedPartsMatrix) {
      return
    }
    const costPerUnit = instance.info?.cost || 0
    const margin = filterMarginsWithEntries(selectedPartsMatrix, costPerUnit)
    const pricePerUnitFromMarkup = roundToTwoDecimal(
      (costPerUnit * 100) / (100 - margin)
    )
    setPrice(pricePerUnitFromMarkup)
  }, [selectedPartsMatrix, instance.info?.cost])

  const addToCart = useCallback(async () => {
    if (!activeCounterSale) {
      const newSale = await createCounterSale({
        authorization: accessToken,
        counterSaleInfo: {
          shop: shopId,
          status: CounterSaleInfoStatusEnum.InProgress
        }
      })
      if ('data' in newSale) {
        await updateInventoryInstance({
          authorization: accessToken,
          inventoryPartInstanceEntry: {
            id: instance.id,
            info: {
              ...instance.info,
              claimedByCounterSale: newSale.data,
              counterSalePricePerUnit: price || 0
            }
          }
        })
      }
    } else {
      await updateInventoryInstance({
        authorization: accessToken,
        inventoryPartInstanceEntry: {
          id: instance.id,
          info: {
            ...instance.info,
            claimedByCounterSale: activeCounterSale.entry?.id,
            counterSalePricePerUnit: price || 0
          }
        }
      })
    }
    closeDialog()
  }, [
    accessToken,
    instance.id,
    instance.info,
    updateInventoryInstance,
    activeCounterSale,
    createCounterSale,
    price,
    shopId,
    closeDialog
  ])

  const { data: vendors } = useGetVendorEntriesByIdQuery(
    {
      authorization: accessToken,
      shop: shopId,
      vendorIds: instance.info?.vendorId ? [instance.info?.vendorId] : []
    },
    {
      skip: !instance.info?.vendorId
    }
  )
  const vendorName = vendors && vendors[0].info?.name

  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell>{row}</TableCell>
      <TableCell>{instance.info?.invoiceNumber}</TableCell>
      {isFluids && (
        <>
          <TableCell>{instance.info?.quantity}</TableCell>
          <TableCell>{instance.info?.unitOfMeasure}</TableCell>
        </>
      )}
      <TableCell>
        <TextFieldNoArrowNoScroll
          variant="standard"
          label="Price"
          value={price}
          error={(price || 0) < 0}
          helperText={(price || 0) < 0 ? 'Price cannot be negative' : ''}
          type="number"
          onChange={(event) => {
            const newValue = parseFloat(event.target.value)
            if (isNaN(newValue)) {
              setPrice('')
            } else {
              setPrice(newValue)
            }
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>
          }}
        />
      </TableCell>
      <TableCell>{vendorName}</TableCell>
      <TableCell>
        <Button
          disabled={isLoadingUpdateInventory || isLoadingCreateCounterSale}
          onClick={addToCart}>
          Add to Cart
        </Button>
      </TableCell>
    </TableRow>
  )
}

export default CounterSaleInstanceItem
